import { useTranslation } from 'react-i18next';
import { XCircle, AlertTriangle, RotateCcw, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ExecutionError } from '../../types';

interface FailureResultProps {
  error: ExecutionError;
  rollbackStatus?: 'completed' | 'failed' | 'partial';
  partiallyCreated?: string[];
  onRetry: () => void;
  onStartOver: () => void;
  canRetry: boolean;
}

export function FailureResult({
  error,
  rollbackStatus,
  partiallyCreated,
  onRetry,
  onStartOver,
  canRetry,
}: FailureResultProps) {
  const { t } = useTranslation('customer-onboarding');

  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6">
      <div className="mb-6 flex items-center gap-3">
        <XCircle className="h-8 w-8 text-destructive" />
        <div>
          <h3 className="text-lg font-semibold text-destructive">
            {t('result.failure.title')}
          </h3>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>

      {(error.entity || error.row || error.details) && (
        <div className="mb-6 rounded-lg bg-white p-4">
          <h4 className="mb-2 font-medium">{t('result.failure.errorDetails')}</h4>
          <dl className="space-y-1 text-sm">
            {error.entity && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('result.failure.entity')}:</dt>
                <dd className="font-medium capitalize">{error.entity}</dd>
              </div>
            )}
            {error.row !== undefined && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('result.failure.row')}:</dt>
                <dd className="font-medium">{error.row}</dd>
              </div>
            )}
            {error.details && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('result.failure.details')}:</dt>
                <dd className="font-medium">{error.details}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {rollbackStatus && (
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('result.failure.rollbackStatus')}:</span>
            <Badge
              variant={
                rollbackStatus === 'completed'
                  ? 'success'
                  : rollbackStatus === 'partial'
                    ? 'warning'
                    : 'danger'
              }
            >
              {rollbackStatus === 'completed' && t('result.failure.rollbackCompleted')}
              {rollbackStatus === 'partial' && t('result.failure.rollbackPartial')}
              {rollbackStatus === 'failed' && t('result.failure.rollbackFailed')}
            </Badge>
          </div>

          {rollbackStatus === 'failed' && (
            <p className="mt-2 text-sm text-amber-700">
              <AlertTriangle className="mr-1 inline h-4 w-4" />
              {t('result.failure.rollbackWarning')}
            </p>
          )}
        </div>
      )}

      {partiallyCreated && partiallyCreated.length > 0 && (
        <div className="mb-6">
          <h4 className="mb-2 text-sm font-medium">{t('result.failure.partiallyCreated')}</h4>
          <div className="flex flex-wrap gap-2">
            {partiallyCreated.map((entity, index) => (
              <Badge key={index} variant="outline">
                {entity}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {canRetry && (
          <Button onClick={onRetry} variant="outline">
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('result.failure.fixRetry')}
          </Button>
        )}
        <Button onClick={onStartOver} variant="destructive">
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('result.failure.startOver')}
        </Button>
      </div>
    </div>
  );
}
